import { Dropdown, DropdownFieldProps, InputChangeHandler, MultiSelectDropdown, useSimpleInputState } from "capitalrx-components-2.0/lib/forms";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Heading } from "capitalrx-components-2.0/lib/typography";
import { FC, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetGroupList, useGetPlanAccountList, useGetPlanBenefitList, useGetPlanList } from "../../hooks/list";
import { PricingLevelsTypes } from "@/types";

interface ExtendedDropdownProps extends DropdownFieldProps {
  onSearchChange?: InputChangeHandler<string | null>;
}

const ExtendedDropdown = (props: ExtendedDropdownProps) => {
  return <Dropdown.Field {...props} />;
};

interface Props {
  readonly defaultValues?: PricingLevelsTypes.PricingLevelBase;
}

export const PricingLevelFormHierarchy: FC<Props> = ({ defaultValues }) => {
  const { reset, resetField, watch } = useFormContext();

  const { columns612 } = useResponsive({
    columns612: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
  });

  /*
    If this is an existing record and values are provided by the server,
    set the initial search values to those values so that the initial
    datasource query results will include the values on the record.
  */
  const [planSearch, setPlanSearch] = useSimpleInputState(defaultValues?.client_id ?? "");
  const [planAccountSearch, setPlanAccountSearch] = useSimpleInputState(defaultValues?.external_account_id ?? "");
  const [groupSearch, setGroupSearch] = useSimpleInputState(defaultValues?.external_group_id ?? "");

  const clientId = useWatch({ name: "client_id" });
  const externalAccountId = useWatch({ name: "external_account_id" });

  const getPlanListResults = useGetPlanList(
    {
      plan_name_or_client_id: planSearch,
    },
    {
      enabled: planSearch.length > 2,
    },
  );

  /*
    Store the plan ID to use in plan account query when the user selects a client ID.

    We need to include the API call hook as a dependency because it may initially run
    before the API query variables are populated and return an empty result set.

    For example, this may occur on initial page render and we might miss finding the plan ID
    for an existing record's client ID.  When the plan list query runs again after seeing the
    defaultValues client ID, we want to run this to update the plan ID accordingly.

    Same logic applies to the plan account ID below (used in the group list API query).
  */
  const planId = useMemo(() => {
    const selectedPlan = getPlanListResults?.data?.results?.find((result) => {
      return result.plan.client_id === clientId;
    });

    return selectedPlan?.plan?.id;
  }, [clientId, getPlanListResults]);


  const getPlanAccountListResults = useGetPlanAccountList(
    {
      plan_id: planId,
      external_account_id: planAccountSearch,
    },
    {
      enabled: !!planId && planAccountSearch.length > 2,
    },
  );

  const planAccountId = useMemo(() => {
    const selectedPlanAccount = getPlanAccountListResults?.data?.results?.find((result) => {
      return result.plan_account.external_account_id === externalAccountId;
    });

    return selectedPlanAccount?.plan_account?.id;
  }, [externalAccountId, getPlanAccountListResults]);

  const getGroupListResults = useGetGroupList(
    {
      plan_id: planId,
      plan_account_id: planAccountId,
      external_group_id: groupSearch,
    },
    {
      enabled: !!planAccountId && groupSearch.length > 2,
    },
  );

  const getPlanBenefitListResults = useGetPlanBenefitList(
    {
      plan_id: planId,
    },
    {
      enabled: !!planId,
    },
  );

  const renderPlanOptions = useMemo(() => {
    return getPlanListResults?.data?.results?.map((result) => (
      <Dropdown.Option key={result.plan.id} value={result.plan.client_id}>
        {`${result.plan.client_id} - ${result.plan.name}`}
      </Dropdown.Option>
    ));
  }, [getPlanListResults]);

  const renderPlanAccountOptions = useMemo(() => {
    return getPlanAccountListResults?.data?.results?.map((result) => (
      <Dropdown.Option key={result.plan_account.external_account_id} value={result.plan_account.external_account_id}>
        {`${result.plan_account.external_account_id} - ${result.plan_account.name}`}
      </Dropdown.Option>
    ));
  }, [getPlanAccountListResults]);

  const renderGroupOptions = useMemo(() => {
    return getGroupListResults?.data?.results?.map((result) => (
      <Dropdown.Option key={result.group.external_group_id} value={result.group.external_group_id}>
        {`${result.group.external_group_id} - ${result.group.group_name}`}
      </Dropdown.Option>
    ));
  }, [getGroupListResults]);

  const renderCoverageStrategyOptions = useMemo(() => {
    return getPlanBenefitListResults?.data?.results?.map((result) => (
      <MultiSelectDropdown.Option key={result.plan_benefit.coverage_strategy_id} value={result.plan_benefit.coverage_strategy_id}>
        {`${result.plan_benefit?.name} | ${result.plan_benefit?.coverage_strategy_id}`}
      </MultiSelectDropdown.Option>
    ));
  }, [getPlanBenefitListResults]);

  /*
    Form field dependencies:
      - external_account_id depends on plan ID (and thus client ID)
      - external_group_id depends on both plan ID and external account ID (and thus plan account ID)
      - coverage_strategy_ids depends on plan ID (and thus client ID)

    When a field changes, all its dependencies should be cleared to avoid creating records
    with invalid combinations of field values.
  */
  useEffect(() => {
    const dependentFieldsAutoClear = watch((data, { name, type }) => {
      if (type !== "change") {
        return;
      }

      if (name === "client_id") {
        resetField("external_account_id", { defaultValue: null });
        resetField("external_group_id", { defaultValue: null });
        resetField("coverage_strategy_ids", { defaultValue: [] });
      } else if (name === "external_account_id") {
        resetField("external_group_id", { defaultValue: null });
      }

      return () => dependentFieldsAutoClear.unsubscribe();
    });
  }, [watch, resetField]);

  useEffect(() => reset(defaultValues), [defaultValues]);

  return (
    <Stack>
      <Heading>Hierarchy</Heading>
      <Grid>
        <Grid.Spread columns={columns612}>
          <ExtendedDropdown // Dropdown.Field
            testId="test__client-id"
            name="client_id"
            label="Client ID"
            placeholder="Select"
            defaultValue={defaultValues?.client_id}
            searchable
            required
            instructions="Must provide more than 2 characters to search"
            onSearchChange={(value, previousValue) => {
              if (previousValue === "" && value === "") {
                // Set the search to previous search if the dropdown is opened again
                setPlanSearch(planSearch, previousValue);
              } else {
                setPlanSearch(value, previousValue);
              }
            }}
          >
            {renderPlanOptions}
          </ExtendedDropdown>

          <ExtendedDropdown
            testId="test__external-account-id"
            name="external_account_id"
            label="External Account ID"
            placeholder="Select"
            defaultValue={defaultValues?.external_account_id}
            readOnly={!clientId as boolean}
            searchable
            instructions="Must provide more than 2 characters to search"
            onSearchChange={(value, previousValue) => {
              if (previousValue === "" && value === "") {
                // Set the search to previous search if the dropdown is opened again
                setPlanAccountSearch(planAccountSearch, previousValue);
              } else {
                setPlanAccountSearch(value, previousValue);
              }
            } }
          >
            {renderPlanAccountOptions}
          </ExtendedDropdown>

          <ExtendedDropdown
            testId="test__external-group-id"
            name="external_group_id"
            label="External Group ID"
            placeholder="Select"
            defaultValue={defaultValues?.external_group_id}
            readOnly={!externalAccountId as boolean}
            searchable
            instructions="Must provide more than 2 characters to search"
            onSearchChange={(value, previousValue) => {
              if (previousValue === "" && value === "") {
                // Set the search to previous search if the dropdown is opened again
                setGroupSearch(groupSearch, previousValue);
              } else {
                setGroupSearch(value, previousValue);
              }
            } }
          >
            {renderGroupOptions}
          </ExtendedDropdown>

          <MultiSelectDropdown.Field
            name="coverage_strategy_ids"
            placeholder="Select"
            testId="test__coverage-strategy-ids"
            label={"Coverage Strategy IDs"}
            readOnly={!clientId as boolean}
            searchable
          >
            {renderCoverageStrategyOptions}
          </MultiSelectDropdown.Field>

          <MultiSelectDropdown.Field
            name="coverage_strategy_types"
            label="Coverage Strategy Types"
            placeholder="Select"
            searchable
            testId="test__coverage_strategy_type"
          >
            <MultiSelectDropdown.Option value="commercial">Commercial</MultiSelectDropdown.Option>
            <MultiSelectDropdown.Option value="exchange">Exchange</MultiSelectDropdown.Option>
            <MultiSelectDropdown.Option value="commercial_exchange">Commercial Exchange</MultiSelectDropdown.Option>
            <MultiSelectDropdown.Group label="Medicaid Strategies">
              <MultiSelectDropdown.Option value="medicaid">Medicaid</MultiSelectDropdown.Option>
            </MultiSelectDropdown.Group>
            <MultiSelectDropdown.Group label="Medicare: MAPD">
              <MultiSelectDropdown.Option value="medicare_defined_standard_mapd">Defined Standard</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_actuarially_equivalent_mapd">Actuarially Equivalent</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_basic_alternative_mapd">Basic Alternative</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_enhanced_alternative_mapd">Enhanced Alternative</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_employer_group_waiver_plan_mapd">EGWP</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_part_b_only_ma">Part B Only</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_pace">PACE</MultiSelectDropdown.Option>
            </MultiSelectDropdown.Group>
            <MultiSelectDropdown.Group label="Medicare: PDP">
              <MultiSelectDropdown.Option value="medicare_defined_standard_pdp">Defined Standard</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_actuarially_equivalent_pdp">Actuarially Equivalent</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_basic_alternative_pdp">Basic Alternative</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_enhanced_alternative_pdp">Enhanced Alternative</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="medicare_employer_group_waiver_plan_pdp">EGWP</MultiSelectDropdown.Option>
            </MultiSelectDropdown.Group>
            <MultiSelectDropdown.Group label="Medicare: Other">
              <MultiSelectDropdown.Option value="medicare_prescription_payment_plan">M3P</MultiSelectDropdown.Option>
            </MultiSelectDropdown.Group>
            <MultiSelectDropdown.Group label="Other">
              <MultiSelectDropdown.Option value="discount_card">Discount Card</MultiSelectDropdown.Option>
              <MultiSelectDropdown.Option value="copay_card">Copay Card</MultiSelectDropdown.Option>
            </MultiSelectDropdown.Group>
          </MultiSelectDropdown.Field>
        </Grid.Spread>
      </Grid>
    </Stack>
  );
};
